"use client";
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Box, Typography, CircularProgress, Grid, Paper, 
  Stack, Divider, Chip, IconButton, Collapse
} from "@mui/material";
import { 
  RateReview, FactCheck, ReportProblem, Shield, 
  KeyboardArrowDown, KeyboardArrowUp, HealthAndSafety 
} from "@mui/icons-material";
import { getCarePlanById } from "../../../../../api/services/carePathWay";
import { QK } from "../../../../../base/qk";
import { CarePlan, RiskProfile } from "./CarePlan";
import ConsentCapacity from "./ConsentCapacity/ConsentCapacity";
import CarePlanReviewSection from "./Review";
import useClientDetails from "../../../../../hooks/useClientDetails";
import AssignAssessmentForm from "./AssignAssessmentForm";
import BodyMap from "../../BodyMap";
import IconCircleButton from "../../../../../lib/Button";
import { BodyMapDialog } from "./CarePlan/Dialog";

const UnifiedSection = ({ title, icon: Icon, children, color, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <Box sx={{ mb: 1 }}>
      <Box 
        onClick={() => setIsOpen(!isOpen)}
        sx={{ 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
          cursor: 'pointer', py: 1.5, px: 1, borderRadius: '12px',
          transition: '0.2s', '&:hover': { bgcolor: '#f1f5f9' }
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: `${color}15`, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>{title}</Typography>
        </Stack>
        <IconButton size="small" sx={{ color: '#94a3b8' }}>
          {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={isOpen}>
        <Box sx={{ pt: 1, pb: 3, px: { xs: 1, md: 2 }, borderLeft: `2px solid ${color}30`, ml: 3 }}>
          {children}
        </Box>
      </Collapse>
      {!isOpen && <Divider sx={{ mx: 1, opacity: 0.5 }} />}
    </Box>
  );
};

const DynamicCarePlanForm = () => {
  const { clientID, carePlanID: routeCarePlanID } = useParams();
  const [responses, setResponses] = useState({});
  const [openBodyMapDialog, setOpenBodyMapDialog] = useState(false);
  const { fullName, birthDate } = useClientDetails(clientID);

  const { data, isLoading } = useQuery(
    [QK.carePlan, routeCarePlanID, clientID],
    () => getCarePlanById(routeCarePlanID, clientID),
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );

  useEffect(() => {
    if (data?.submission?.responses) {
      setResponses(data.submission.responses);
    }
  }, [data?.submission?.id, data?.submission?.responses]);

  const handleChange = useCallback((key, value) => {
    setResponses((prev) => ({ ...prev, [key]: value }));
  }, []);

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress size={32} sx={{ color: '#6366f1' }} />
    </Box>
  );

  // carePlanID priority: data?.id (from API) or routeCarePlanID (from URL params)
  const carePlanId = data?.id || routeCarePlanID;

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", p: { xs: 1.5, md: 3 } }}>
      <Grid container spacing={4}>
        
        <Grid size={{ xs: 12, md: 8 }}>
          <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', px: 1 }}>
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 54, height: 54, borderRadius: '16px', bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                <HealthAndSafety sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 1000, color: '#0f172a' }}>{data?.title || "Care Plan"}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#475569' }}>{fullName} | DOB: {birthDate}</Typography>
              </Box>
            </Stack>
            <Chip label="DRAFTING MODE" size="small" sx={{ fontWeight: 900, bgcolor: '#334155', color: '#fff' }} />
          </Box>

          <Stack spacing={1}>
            <UnifiedSection title="Review Summary" defaultOpen={false} icon={RateReview} color="#6366f1">
              <CarePlanReviewSection responses={responses} handleChange={handleChange} carePlanID={data?.id} />
            </UnifiedSection>

            <UnifiedSection title="Care Plan Details" icon={FactCheck} color="#0ea5e9" defaultOpen={true}>
              <CarePlan 
                sections={data?.sections || []} 
                responses={responses} 
                handleChange={handleChange} 
                fullData={data} 
                clientID={clientID}
              />
            </UnifiedSection>

            <UnifiedSection title="Risk Profile Matrix" icon={ReportProblem} color="#f43f5e">
              <RiskProfile carePlanID={data?.id} />
            </UnifiedSection>

            <UnifiedSection title="Consent & Capacity" icon={Shield} color="#10b981">
              <ConsentCapacity carePlanID={data?.id} />
            </UnifiedSection>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ position: "sticky", top: 20 }}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: "20px", border: "1px solid #e2e8f0" }}>
              {/* carePlanId পাঠানো হচ্ছে */}
              <AssignAssessmentForm carePlanID={carePlanId} />
              <Divider sx={{ my: 3 }} />
              <Stack direction="row" justifyContent="space-between" mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>Clinical Body Map</Typography>
                <IconCircleButton size="small" onClick={() => setOpenBodyMapDialog(true)} />
              </Stack>
              <BodyMap clientID={clientID} carePlanID={data?.id} />
            </Paper>
          </Box>
        </Grid>
      </Grid>
      
      <BodyMapDialog 
        open={openBodyMapDialog} 
        handleClose={() => setOpenBodyMapDialog(false)} 
        clientID={clientID} 
        carePlanID={data?.id} 
      />
    </Box>
  );
};

export default DynamicCarePlanForm;